import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isCachedLibraryCover } from "@/lib/libraryCovers";
import type { LibrarySearchResult } from "@/lib/library";
import { CoverThumb } from "./CoverThumb";
import { CoverUploadButton } from "./CoverUploadButton";

type DraftLike = LibrarySearchResult & { notes?: string | null };

const describeCoverResolution = (url: string | null | undefined): string | null => {
  if (!url) return null;
  if (url.startsWith("library-cover://")) return "Cached high-resolution copy stored in your library.";
  if (/\/covers\/original\//i.test(url) || /[?&]key=/.test(url)) return "High-resolution publisher cover (full original will be saved).";
  if (/images\.isbndb\.com\/covers\//i.test(url)) return "Thumbnail-resolution cover (no high-res available for this ISBN).";
  if (/covers\.openlibrary\.org/i.test(url)) return "Open Library cover.";
  if (/books\.google\.com|googleusercontent/i.test(url)) return "Google Books cover.";
  return "Custom cover URL.";
};

export const CoverSection = ({
  draft,
  idPrefix,
  libraryItemId,
  onUpdateDraft,
  onTextField,
}: {
  draft: DraftLike;
  idPrefix: string;
  libraryItemId: string | null | undefined;
  onUpdateDraft: (updater: (current: DraftLike) => DraftLike) => void;
  onTextField: (key: keyof DraftLike, value: string) => void;
}) => {
  const coverCandidates = (draft.coverCandidates ?? []).filter(Boolean);
  const allCovers = Array.from(
    new Set([draft.coverUrl, ...coverCandidates].filter((value): value is string => !!value)),
  );
  const coverResolutionLabel = describeCoverResolution(draft.coverUrl);

  const handleCoverUploaded = (ref: string) => {
    onUpdateDraft((current) => {
      const existing = (current.coverCandidates ?? []).filter(Boolean);
      const candidates = existing.includes(ref) ? existing : [ref, ...existing];
      return { ...current, coverUrl: ref, coverCandidates: candidates };
    });
  };

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Cover image</Label>
        {allCovers.length > 1 && (
          <span className="text-[11px] text-muted-foreground">{allCovers.length} options · click to swap</span>
        )}
      </div>
      <div className="flex flex-wrap items-start gap-3">
        {draft.coverUrl ? (
          <div className="shrink-0">
            <div className="h-48 w-32 overflow-hidden rounded border border-border bg-background">
              <CoverThumb url={draft.coverUrl} alt="Selected cover preview" className="h-full w-full object-contain" />
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">Selected cover</p>
          </div>
        ) : (
          <div className="shrink-0">
            <div className="flex h-48 w-32 items-center justify-center rounded border border-dashed border-border bg-background text-[11px] text-muted-foreground">
              No cover yet
            </div>
          </div>
        )}
        {allCovers.length > 1 ? (
          <div className="flex flex-wrap gap-2">
            {allCovers.map((url) => {
              const selected = url === draft.coverUrl;
              return (
                <button
                  key={url}
                  type="button"
                  onClick={() => onUpdateDraft((current) => ({ ...current, coverUrl: url }))}
                  className={
                    selected
                      ? "relative h-24 w-16 overflow-hidden rounded ring-2 ring-primary ring-offset-2 ring-offset-background"
                      : "relative h-24 w-16 overflow-hidden rounded opacity-70 transition hover:opacity-100"
                  }
                  title={selected ? "Selected cover" : "Use this cover"}
                >
                  <CoverThumb url={url} alt="" className="h-full w-full object-contain" />
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      {coverResolutionLabel && (
        <p className="text-[11px] text-muted-foreground">{coverResolutionLabel}</p>
      )}
      <CoverUploadButton libraryItemId={libraryItemId ?? null} onUploaded={handleCoverUploaded} />
      <div className="space-y-1">
        <Label htmlFor={`${idPrefix}-cover-url`} className="text-xs text-muted-foreground">Or paste a custom cover URL</Label>
        <Input
          id={`${idPrefix}-cover-url`}
          type="url"
          placeholder="https://…"
          value={isCachedLibraryCover(draft.coverUrl) ? "" : (draft.coverUrl ?? "")}
          onChange={(e) => onTextField("coverUrl", e.target.value)}
        />
        {isCachedLibraryCover(draft.coverUrl) ? (
          <p className="text-[10px] text-muted-foreground">A cached copy is in use. Paste a URL or upload a new image to replace it.</p>
        ) : null}
      </div>
    </div>
  );
};
